import classNames from "classnames";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  ErrorResponse,
  SalesDashboardData,
  SalesRecord,
} from "../../../data-store";
import useSalesDashboardTimeSeriesData from "../../../data-store/useSalesDashboardTimeSeriesData";
import {
  ConvertableUnit,
  convertToTargetUnit,
  MassUnit,
  massUnits,
} from "../../../util/units";
import StatusDisplay from "../../StatusDisplay";
import { Panel } from "../../utils/Panel";
import "../SalesDashboardPage.css";
import ImpactCategoryChart from "../ImpactCategoryChart";
import { RatingDistributionChart } from "../RatingDistributionChart";
import ChartsOverTimeController from "./ChartsOverTimeController";

type PeriodsOptions = "MONTHLY" | "QUARTERLY" | "YEARLY";

interface ChartsOverTimeProps {}

const ChartsOverTime: React.FC<PropsWithChildren<ChartsOverTimeProps>> = () => {
  const intl = useIntl();
  const [salesDashboardDataStatus] = useSalesDashboardTimeSeriesData();

  const availableCharts = ["IMPACT_CATEGORY", "RATING_DISTRIBUTION"];

  const availablePeriods: {
    label: string;
    value: PeriodsOptions;
  }[] = useMemo(() => {
    if (
      salesDashboardDataStatus.type !== "success" ||
      !salesDashboardDataStatus.value?.success
    )
      return [];
    return [
      ...(salesDashboardDataStatus?.value?.monthlyData
        ? [
            {
              label: intl.formatMessage({
                id: "components/pages:SalesDashBoard/monthly",
                defaultMessage: "Monthly",
              }),
              value: "MONTHLY" as PeriodsOptions,
            },
          ]
        : []),
      // Deprioritized for now
      ...(salesDashboardDataStatus.value?.quarterlyData
        ? [
            {
              label: intl.formatMessage({
                id: "components/pages:SalesDashBoard/quarterly",
                defaultMessage: "Quarterly",
              }),
              value: "QUARTERLY" as PeriodsOptions,
            },
          ]
        : []),
      {
        label: intl.formatMessage({
          id: "components/pages:SalesDashBoard/yearly",
          defaultMessage: "Yearly",
        }),
        value: "YEARLY",
      },
    ];
  }, [intl, salesDashboardDataStatus]);

  const [selectedPeriod, setSelectedPeriod] = useState<
    "MONTHLY" | "QUARTERLY" | "YEARLY"
  >(availablePeriods?.[0]?.value);
  const [selectedChart, setSelectedChart] = useState(availableCharts[0]);
  const [selectedYear, setSelectedYear] = useState<{
    id: string;
    label: string;
  } | null>(null);

  useEffect(() => {
    if (availablePeriods.length) {
      setSelectedPeriod(availablePeriods?.[0]?.value);
    }
  }, [availablePeriods]);

  const handleChangeChart = useCallback((e) => {
    setSelectedChart(e);
  }, []);
  const handleChangePeriod = useCallback((e) => {
    setSelectedPeriod(e);
  }, []);
  const handleChangeYear = useCallback((e) => {
    setSelectedYear(e);
  }, []);

  const RENDERED_CHART = useCallback(
    (sales: SalesDashboardData) => {
      if (selectedYear == null || selectedChart == null) return;

      const convertGHG = (item: SalesRecord) => {
        return {
          ...item,
          ghgEmissions: convertToTargetUnit(
            item.ghgEmissions,
            item.ghgEmissionsUnit.find((i) =>
              massUnits.includes(i as MassUnit)
            ) as ConvertableUnit,
            "t"
          ),
        };
      };

      const filteredData = {
        monthlyData:
          selectedYear.id === "ALL"
            ? sales?.monthlyData
            : sales?.monthlyData
                ?.filter(
                  (item: any) =>
                    item.salesDataTimePeriod.year === Number(selectedYear.id)
                )
                ?.map(convertGHG),
        quarterlyData:
          selectedYear.id === "ALL"
            ? sales?.quarterlyData
            : sales?.quarterlyData
                ?.filter(
                  (item: any) =>
                    item.salesDataTimePeriod.year === Number(selectedYear.id)
                )
                ?.map(convertGHG),
        yearlyData: sales?.yearlyData?.map(convertGHG),
      };

      if (selectedChart === "IMPACT_CATEGORY") {
        return (
          <ImpactCategoryChart
            isAllSelected={selectedYear.id === "ALL"}
            period={selectedPeriod}
            data={filteredData as SalesDashboardData}
          />
        );
      }

      if (selectedChart === "RATING_DISTRIBUTION") {
        return (
          <RatingDistributionChart
            isAllSelected={selectedYear.id === "ALL"}
            period={selectedPeriod}
            data={filteredData as SalesDashboardData}
          />
        );
      }

      return (
        <ImpactCategoryChart
          isAllSelected={selectedYear.id === "ALL"}
          period={selectedPeriod}
          data={filteredData as SalesDashboardData}
        />
      );
    },
    [selectedYear, selectedChart, selectedPeriod]
  );

  const getAvailableYears = useCallback(
    (data: SalesDashboardData): { id: string; label: string }[] => {
      const allYears = new Set<number>();

      const extractYears = (records?: SalesRecord[]) => {
        records?.forEach((item) => {
          if (item.salesDataTimePeriod?.year) {
            allYears.add(item.salesDataTimePeriod.year);
          }
        });
      };

      extractYears(data?.monthlyData);
      extractYears(data?.quarterlyData);
      extractYears(data?.yearlyData);
      return [
        {
          id: "ALL",
          label: intl.formatMessage({
            id: "components/pages:SalesDashBoard/AllTime",
            defaultMessage: "All time",
          }),
        },
        ...Array.from(allYears)
          .sort((a, b) => b - a)
          .map((label) => ({
            id: String(label),
            label: String(label),
          })),
      ];
    },
    [intl]
  );

  const availableYears: { id: string; label: string }[] = useMemo(() => {
    if (
      salesDashboardDataStatus.type !== "success" ||
      salesDashboardDataStatus?.value == null ||
      !salesDashboardDataStatus.value.success
    )
      return [];
    return getAvailableYears(salesDashboardDataStatus?.value);
  }, [getAvailableYears, salesDashboardDataStatus]);

  useEffect(() => {
    setSelectedYear(availableYears[0]);
  }, [availableYears]);

  return (
    <div
      className={classNames("SalesDashboardCard", {
        SalesDashboardCard_loading: salesDashboardDataStatus.type === "loading",
      })}
    >
      <StatusDisplay.Many<[SalesDashboardData | ErrorResponse | null]>
        statuses={[salesDashboardDataStatus]}
      >
        {(sales) => {
          if (sales == null) return;
          if (sales.success === false) {
            if (sales.error.code === "CACHED_IMPACT_NOT_FOUND") {
              return (
                <Panel>
                  <div className="p-5 text-center">
                    <FormattedMessage
                      id="components/sales-data/SalesDataPage:cachedImpactNotFound"
                      defaultMessage="We are currently unable to display your sales data. Some calculations may still be running. Please try again later."
                    />
                  </div>
                </Panel>
              );
            }
          } else {
            if (selectedYear == null) return;
            return (
              <div>
                <ChartsOverTimeController
                  handleChangeChart={handleChangeChart}
                  handleChangePeriod={handleChangePeriod}
                  selectedChart={selectedChart}
                  availableCharts={availableCharts}
                  availablePeriods={availablePeriods}
                  selectedPeriod={selectedPeriod}
                  availableYears={availableYears}
                  selectedYear={selectedYear}
                  handleChangeYear={handleChangeYear}
                />
                {RENDERED_CHART(sales)}
              </div>
            );
          }
        }}
      </StatusDisplay.Many>
    </div>
  );
};

export default ChartsOverTime;
